import { ExecutionContext } from 'styled-components';
import { DefaultTheme } from 'styled-components';
import { NestedKeyOf } from '../Icons/HudIcons';
import { getByPath } from '../utils';

const turquoise = {
  primary: '#0ffee6',
  secondary: '#0fe8d3',
  tertiary: '#0dc5b3',
  quaternary: '#132E2E',
  quinary: '#215656',
};

export const darkColors: DefaultTheme = {
  name: 'dark',
  textPrimary: '#ffffff',
  white: '#ffffff',
  textSecondary: '#D2D6D7',
  textTertiary: '#949494',
  backgroundDark: '#111315',
  backgroundLight: '#1E1F21',
  strokeLight: '#434547',
  buttonBackground: '#292B2E',
  buttonBackgroundHover: '#363A3D',
  strokePrimary: '#434547',
  strokeSecondary: '#434547',
  turquoise: turquoise.primary,
  turquoiseSecondary: turquoise.secondary,
  turquoiseTertiary: turquoise.tertiary,
  turquoiseQuaternary: turquoise.quaternary,
  turquoiseQuinary: turquoise.quinary,
  positivePrimary: '#5fffbc',
  positiveSecondary: '#141616',
  purpleHome: '#b65aff',
  blue: '#257CFF',
  red: '#ff2e60',
  redSecondary: '#371115',
  gray: '#747677',
  headerGray: '#656A78',
  transparent: 'transparent',
 
  glowGradient: 'linear-gradient(90deg, #18BEAE -47.46%, #0EFFE7 100%)',
  glowPressed: 'linear-gradient(90deg, #13998C -47.46%, #0CD9C4 100%)',
  glowBoxShadow: '0px 0px 24px -3px rgba(15, 254, 230, 0.70)',

  linkColor: '#b1fcf8',

  tooltipBackground: '#292B2E',
  tooltipForeground: '#ffffff',
  tooltipBorder: '#292B2E',

  separator: '#3c3c3c',

  nodeBackground: '#3f3f3f',
  nodeTextRainbow: 'linear-gradient(90deg, #ad86ed 0%, #25fce7 100%)',

  tableContainerBackground: '#1E1F21',
  tableGradient:
  'linear-gradient(90deg, rgba(16, 16, 16, 0.00) -8.85%, #1E1F21 67.56%)',

  tableHeaderBackground: '#363A3D',
  tableHeaderForeground: '#D2D6D7',
  tableHeaderSortAccessor: '#808080',
  tableRowAlternateColor: '#292B2E'
};

export const lightColors: DefaultTheme = {
  ...darkColors,
  name: 'light',
  textPrimary: '#212529',
  textTertiary: '#949494',
  textSecondary: '#6C757D',
  backgroundDark: '#F9F9F9',
  backgroundLight: '#FFFFFF',
  buttonBackground: '#F2F4F5',
  buttonBackgroundHover: '#EBEEF0',
  strokePrimary: '#D2D6D7',
  strokeSecondary: '#EBEEF0',
  tableGradient:
    'linear-gradient(90deg, rgba(255, 255, 255, 0.00) -8.85%, #FFF 67.56%)',

  tooltipBackground: '#2D2C2C',
  tooltipForeground: '#ffffff',
  tooltipBorder: '#404040',

  separator: '#dfdfdf',

  linkColor: '#39b3a7',

  nodeBackground: '#DFDFDF',
  nodeTextRainbow: 'linear-gradient(90deg, #ad86ed 0%, #0DC5B3 100%)',
};

const vscodeDarkColors = {
  ...darkColors,
  name: 'vscodeDark',
  textPrimary: 'var(--vscode-foreground, #cccccc)',
  linkColor: '#b1fcf8',
  tooltipBackground: 'var(--vscode-editorHoverWidget-background, #202020)',
  tooltipForeground: 'var(--vscode-editorHoverWidget-foreground, #c7c7c7)',
  tooltipBorder: 'var(--vscode-editorHoverWidget-border, #464646)',
  selectionBackground: 'var(--vscode-selection-background, #6984a9)',

  treeSelectedBackground:
    'var(--vscode-list-activeSelectionBackground, #04395e)',

  treeFocusBackground: 'inherit',
  treeFont: 'inherit',
  treeTextColor: 'inherit',
};

const vscodeLightColors = {
  ...vscodeDarkColors,
  ...lightColors,
  textPrimary: 'var(--vscode-foreground, #202020)',
  linkColor: '#0DC5B3',
  name: 'vscodeLight',
};

const jetbrainsDarkColors = {
  ...vscodeDarkColors,
  name: 'jetbrainsDark',

  nodeBackground: '#ffffff26',
  textPrimary: '#dddfe2',
  tooltipBackground: darkColors.tooltipBackground,
  tooltipForeground: darkColors.tooltipForeground,
  tooltipBorder: darkColors.tooltipBorder,
};

const jetbrainsLightColors = {
  ...vscodeLightColors,
  name: 'jetbrainsLight',
};

const vscodeColors = {
  dark: vscodeDarkColors,
  light: vscodeLightColors,
};

const jetbrainsColors = {
  dark: jetbrainsDarkColors,
  light: jetbrainsLightColors,
};

export const webviewThemes = {
  vscode: vscodeColors,
  jetbrains: jetbrainsColors,
};

export type ThemeColorName = NestedKeyOf<DefaultTheme>;

export const themeColor = (
  colorName: ThemeColorName | { dark: ThemeColorName; light: ThemeColorName },
) => {
  return (props: ExecutionContext) => {
    if (typeof colorName === 'object') {
      const darkColor = getByPath(props.theme, colorName.dark) as string;
      const lightColor = getByPath(props.theme, colorName.light) as string;
      return props.theme.name === 'dark' ? darkColor : lightColor;
    } else {
      return getByPath(props.theme, colorName) as string;
    }
  };
};
